import type { Id } from "@convex/_generated/dataModel";
import type { UseMutationResult } from "@tanstack/react-query";
import type { UseFormReturn } from "react-hook-form";

import { toConvexTableId } from "~/lib/convex-table-id";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../ui/accordion";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Label } from "../../../ui/label";
import { MultiCombobox } from "../../../ui/multi-combobox";
import type {
  CategoryForMenu,
  ItemDataForForm,
  ItemFormData,
  ItemMutationData,
} from "./item-form-types";
import { buildMutationData } from "./item-form-utils";

interface SharedItemDialogProps {
  itemData: ItemDataForForm;
  categories: CategoryForMenu[];
  form: UseFormReturn<ItemFormData, unknown, ItemFormData>;
  updatingSharedItemStatus: "confirm" | "select-categories" | null;
  setUpdatingSharedItemStatus: (
    status: "confirm" | "select-categories" | null
  ) => void;
  menuCategoriesToCopyItem: string[];
  setMenuCategoriesToCopyItem: (categories: string[]) => void;
  updateItemMutation: UseMutationResult<
    { success: boolean },
    Error,
    { itemId: Id<"items">; data: ItemMutationData }
  >;
  cloneAndUpdateItemMutation: UseMutationResult<
    { id: string } | null,
    Error,
    {
      id: Id<"items">;
      data: ItemMutationData;
      categoryIds: Id<"categories">[];
    }
  >;
  onGoBack: () => void;
}

export const SharedItemDialog = ({
  itemData,
  categories,
  form,
  updatingSharedItemStatus,
  setUpdatingSharedItemStatus,
  menuCategoriesToCopyItem,
  setMenuCategoriesToCopyItem,
  updateItemMutation,
  cloneAndUpdateItemMutation,
  onGoBack,
}: SharedItemDialogProps) => {
  if (!updatingSharedItemStatus) {
    return null;
  }

  return (
    <Dialog onOpenChange={() => setUpdatingSharedItemStatus(null)} open>
      <DialogContent className="gap-5">
        {updatingSharedItemStatus === "confirm" ? (
          <>
            <DialogHeader className="items-center space-y-0">
              <DialogTitle className="text-foreground">
                Updating a shared item
              </DialogTitle>
              <DialogDescription className="body text-muted-foreground text-center">
                This item is shared with other categories. Do you want to save
                changes to this item for all shared categories?
              </DialogDescription>
            </DialogHeader>
            <Accordion type="multiple">
              <AccordionItem
                className="border-none"
                value="affected-categories"
              >
                <AccordionTrigger className="label text-foreground hover:no-underline">
                  Affected Categories ({itemData.categoryRelations.length})
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  {itemData.categoryRelations.map((category) => (
                    <div
                      className="bg-muted gap-3 rounded-2xl px-4 py-3"
                      key={category.category._id}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-foreground">
                          {category.category.name}
                        </p>
                        <div className="caption bg-info/10 text-info rounded-full px-2 py-1">
                          {category.category.itemCount} items
                        </div>
                      </div>
                      <p className="body-sm text-muted-foreground">
                        {category.category.menuRelations
                          .map((m) => m.menu.name)
                          .join(", ")}
                      </p>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <p className="body-sm text-muted-foreground text-center">
              Only updating this item for one category? Select Save Changes as
              New Item instead.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => {
                  setUpdatingSharedItemStatus("select-categories");
                }}
                variant="secondary"
              >
                Save Changes as New Item
              </Button>
              <Button
                onClick={async () => {
                  if (updateItemMutation.isPending) {
                    return;
                  }
                  setUpdatingSharedItemStatus(null);
                  try {
                    await updateItemMutation.mutateAsync({
                      itemId: itemData.id,
                      data: buildMutationData(form.getValues()),
                    });
                    onGoBack();
                  } catch (error) {
                    if (
                      error instanceof Error &&
                      error.message?.startsWith("Item name already exists")
                    ) {
                      form.setError("name", {
                        message: error.message,
                        type: "value",
                      });
                    }
                  }
                }}
                variant="default"
              >
                Update all categories
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="items-center space-y-0">
              <DialogTitle className="text-foreground">
                Save Changes as New Item
              </DialogTitle>
              <DialogDescription className="body text-muted-foreground text-center">
                Select categories to add the new item
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>Add to categories</Label>
              <MultiCombobox
                groups={["default"]}
                items={{
                  default: categories
                    ? categories.map((category) => ({
                        value: category.categoryId,
                        label: category.name,
                        component: (
                          <div className="w-full space-y-1">
                            <div className="flex w-full items-baseline gap-1">
                              <p className="text-foreground">{category.name}</p>
                              {category.itemCount !== 0 && (
                                <p className="body-sm text-muted-foreground">
                                  {category.itemCount} item
                                  {category.itemCount !== 1 && "s"}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {category.menuRelations?.map((menuRelation) => (
                                <div
                                  className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                                  key={menuRelation.menu.name}
                                >
                                  {menuRelation.menu.name}
                                </div>
                              ))}
                            </div>
                          </div>
                        ),
                      }))
                    : [],
                }}
                onValueChange={(d) => setMenuCategoriesToCopyItem(d.default)}
                values={{
                  default: menuCategoriesToCopyItem,
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => {
                  setMenuCategoriesToCopyItem([]);
                  setUpdatingSharedItemStatus("confirm");
                }}
                variant="secondary"
              >
                Back
              </Button>
              <Button
                onClick={async () => {
                  if (cloneAndUpdateItemMutation.isPending) {
                    return;
                  }
                  setUpdatingSharedItemStatus(null);
                  try {
                    await cloneAndUpdateItemMutation.mutateAsync({
                      id: itemData.id,
                      data: buildMutationData(form.getValues()),
                      categoryIds: menuCategoriesToCopyItem.map((id) =>
                        toConvexTableId("categories", id)
                      ),
                    });
                    onGoBack();
                  } catch (error) {
                    if (
                      error instanceof Error &&
                      error.message?.startsWith("Item name already exists")
                    ) {
                      form.setError("name", {
                        message: error.message,
                        type: "value",
                      });
                    }
                  }
                }}
                variant="default"
              >
                Save New Item
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
